import React, { useContext, useEffect, useState, memo } from 'react';
import { Button, FormControl, Modal } from 'react-bootstrap';
import {
    faArrowLeft,
    faCheck,
    faMinusCircle,
    faPlusCircle,
    faTimes,
    faTrashAlt
} from '@fortawesome/free-solid-svg-icons';

import axios from 'axios';
import { useAlert } from 'react-alert';
import DeleteOrderLine from '../../../common/delete-order-line/DeleteOrderLine';
import ProductInformation from '../../../common/product-information/ProductInformation';
import AuthContext from '../../../authentication/AuthContext';
import OrderContext from '../OrderContext';
import UserContext from '../../../user/UserContext';
import { IconWrapper } from '../../../common/IconWrapper';

export default memo(function AcceptedOrderLineRow({ orderLine, code, disableEditing, showDeleteIcon }) {
    let alert = useAlert();
    const cas = useContext(AuthContext);
    const { userDetails } = useContext(UserContext);
    const { orderId, orderHeader, refreshOrderInfo } = useContext(OrderContext);
    const [quantity, setQuantity] = useState(orderLine.quantity);
    const [highlightQuantity, setHighlightQuantity] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [showRejectionReason, setShowRejectionReason] = useState(false);

    useEffect(() => {
        setQuantity(orderLine.quantity);
        setHighlightQuantity(false);
    }, [orderLine]);

    const numberToPrice = numberField => {
        return '£' + numberField.toFixed(2);
    };

    const isPromo = orderLine.costPromotion || orderLine.rspPromotion;

    let decreaseQuantity = () => {
        if (quantity > 1) {
            setHighlightQuantity(true);
            setQuantity(quantity - 1);
            axios
                .post(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.decrease']
                        .replace('{id}', orderId)
                        .replace('{lineId}', orderLine.orderLineId),
                    {},
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(refreshOrderInfo)
                .catch(() => alert.error('Unable to decrease product quantity'));
        }
        if (quantity === 1) {
            setShowDeleteConfirmation(true);
        }
    };

    let increaseQuantity = () => {
        setHighlightQuantity(true);
        setQuantity(quantity + 1);
        axios
            .post(
                window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.increase']
                    .replace('{id}', orderId)
                    .replace('{lineId}', orderLine.orderLineId),
                {},
                {
                    headers: {
                        Authorization: cas,
                        'Content-Type': 'application/json'
                    }
                }
            )
            .then(refreshOrderInfo)
            .catch(() => alert.error('Unable to increase product quantity'));
    };

    let updateQuantity = () => {
        setHighlightQuantity(true);
        let newQuantity = quantity;
        if (orderLine.supplierMaxOrderQuantity && quantity > orderLine.supplierMaxOrderQuantity) {
            newQuantity = orderLine.supplierMaxOrderQuantity;
            setQuantity(orderLine.supplierMaxOrderQuantity);
        }
        if (newQuantity > 0) {
            axios
                .put(
                    window.CONFIG['ws.activoc-web-order-api.order.lines.quantity.set']
                        .replace('{id}', orderId)
                        .replace('{lineId}', orderLine.orderLineId),
                    { id: orderId, quantity: newQuantity },
                    {
                        headers: {
                            Authorization: cas,
                            'Content-Type': 'application/json'
                        }
                    }
                )
                .then(refreshOrderInfo)
                .catch(() => alert.error('Unable to add product line'));
        } else {
            setShowDeleteConfirmation(true);
        }
    };

    return (
        <>
            <tr className={isPromo ? 'promo-row' : undefined}>
                <td className={'image-column centered'}>
                    <ProductInformation
                        url={window.CONFIG['ws.activoc-web-order-api.products.full']
                            .replace('{productCode}', code)
                            .replace('{orderId}', orderId)}
                        image={orderLine.image}
                        description={orderLine.description}
                    />
                </td>
                <td>{orderLine.source}</td>
                <td>{code}</td>
                <td>
                    <p>
                        <span className={'promo-icon'}>{isPromo && 'P'}</span>
                        {orderLine.description != null ? orderLine.description.toUpperCase() : `\u2013`}
                    </p>
                    <p className={'barcode'}>{orderLine.barcode}</p>
                </td>
                <td>{orderLine.unitSize !== '' ? orderLine.unitSize : `\u2013`}</td>
                <td className={isPromo ? 'promo-cost' : 'greenField'}>
                    {orderLine.cost != null ? numberToPrice(orderLine.cost) : `\u2013`}
                </td>
                <td className={'greenField'}>
                    <p>{orderLine.rsp != null ? numberToPrice(orderLine.rsp) : `\u2013`}</p>
                    <p className={'barcode greenField'}>
                        {orderLine.maximumAllowableRsp != null &&
                            numberToPrice(orderLine.maximumAllowableRsp) + ' MRP'}
                    </p>
                </td>
                <td className={'greenField'}>
                    {orderLine.margin != null ? orderLine.margin.toFixed(2) + '%' : `\u2013`}
                </td>
                {orderHeader.orderStatus !== 'OPEN' && (
                    <td className={'centered'}>{orderLine.orderedQuantity}</td>
                )}
                {(orderHeader.orderStatus !== 'OPEN' || disableEditing) && (
                    <td className={'centered ' + (orderLine.acceptedQuantity === 0 ? 'strike' : '')}>
                        {orderLine.acceptedQuantity}
                    </td>
                )}
                {(orderHeader.orderStatus === 'DELIVERED' || orderHeader.orderStatus === 'INVOICED') && (
                    <td className={'centered ' + (orderLine.deliveredQuantity === 0 ? 'strike' : '')}>
                        {orderLine.deliveredQuantity}
                    </td>
                )}
                {orderHeader.orderStatus === 'OPEN' && !disableEditing && (
                    <td className={'centered'}>
                        <div className={'flex quantity-column'}>
                            <div className={'quantity-selector flex'}>
                                <Button
                                    id={'qty-decrease'}
                                    onClick={decreaseQuantity}
                                    data-testid={'decrease'}
                                    tabIndex={-1}
                                    disabled={quantity <= 0}
                                >
                                    <IconWrapper icon={faMinusCircle} />
                                </Button>
                                <FormControl
                                    type={'text'}
                                    maxLength={'3'}
                                    size={'3'}
                                    value={quantity}
                                    onChange={event => {
                                        let newQuantity = event.target.value;
                                        if (newQuantity === '' || isNaN(newQuantity)) {
                                            newQuantity = 0;
                                        }
                                        if (newQuantity.length > 1 && newQuantity.startsWith('0')) {
                                            newQuantity = newQuantity.substring(1, newQuantity.length);
                                        }
                                        setQuantity(Number(newQuantity));
                                    }}
                                    onBlur={updateQuantity}
                                    className={
                                        (highlightQuantity ? 'quantity-highlight ' : '') +
                                        (orderLine.supplierMaxOrderQuantity &&
                                        quantity > orderLine.supplierMaxOrderQuantity
                                            ? 'error'
                                            : '')
                                    }
                                    onFocus={event => event.target.select()}
                                />
                                <Button
                                    id={'qty-increase'}
                                    onClick={increaseQuantity}
                                    data-testid={'increase'}
                                    tabIndex={-1}
                                    disabled={
                                        orderLine.supplierMaxOrderQuantity &&
                                        quantity >= orderLine.supplierMaxOrderQuantity
                                    }
                                >
                                    <IconWrapper icon={faPlusCircle} />
                                </Button>
                            </div>
                            {orderLine.supplierMaxOrderQuantity ? (
                                <span
                                    className={
                                        quantity > orderLine.supplierMaxOrderQuantity
                                            ? 'error-max'
                                            : undefined
                                    }
                                >
                                    Max: {orderLine.supplierMaxOrderQuantity}
                                </span>
                            ) : (
                                ''
                            )}
                        </div>
                    </td>
                )}
                <td className={'centered statusField'}>
                    {orderLine.source === 'PRESELL' ? (
                        <>
                            ADD <IconWrapper icon={faArrowLeft} className={'valid'} />
                        </>
                    ) : orderLine.rejectionCode != null ? (
                        <>
                            {orderLine.rejectionCode + ' '}
                            <IconWrapper
                                icon={faTimes}
                                className={'invalid ' + (orderLine.rejectionReason ? 'pointer' : '')}
                                onClick={() => {
                                    if (orderLine.rejectionReason) {
                                        setShowRejectionReason(!showRejectionReason);
                                    }
                                }}
                                data-testid={'rej-click'}
                            />
                        </>
                    ) : (
                        <IconWrapper icon={faCheck} className={'valid'} />
                    )}
                </td>
                {orderHeader.orderStatus === 'OPEN' && !disableEditing && showDeleteIcon && (
                    <td
                        className={'pointer'}
                        onClick={() => setShowDeleteConfirmation(true)}
                        data-testid={'delete'}
                    >
                        <IconWrapper icon={faTrashAlt} />
                    </td>
                )}
                <Modal
                    show={showDeleteConfirmation}
                    size="lg"
                    onHide={() => setShowDeleteConfirmation(false)}
                >
                    <DeleteOrderLine
                        setShowDeleteConfirmation={setShowDeleteConfirmation}
                        image={orderLine.image}
                        code={code}
                        description={orderLine.description}
                        orderId={orderId}
                        orderLineId={orderLine.orderLineId}
                        cas={cas}
                        refreshOrderInfo={refreshOrderInfo}
                    />
                </Modal>
            </tr>
            {showRejectionReason && (
                <tr className={'rejection-reason'} data-testid={'rejection-reason'}>
                    <td colSpan={11} className={'right-align'}>
                        {orderLine.rejectionReason}
                        {userDetails.telesales &&
                            orderLine.rejectionReasonString !== null &&
                            ' | ' + orderLine?.rejectionReasonString}
                    </td>
                </tr>
            )}
        </>
    );
});
